import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../../contexts/AuthContext';
import './PortalGuidePage.css';

export const PortalGuidePage: React.FC = () => {
	const { currentAgent } = useAuth();
	const navigate = useNavigate();
	const [activeSection, setActiveSection] = useState('dashboard-widgets');
	const [showBackToTop, setShowBackToTop] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const isMasterAgent = currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent';
	const isSuperAgent = currentAgent?.role === 'super_agent';

	useEffect(() => {
		// Find the scrolling container (dashboard-content)
		const scrollContainer = document.querySelector('.dashboard-content');
		if (!scrollContainer) return;

		const handleScroll = () => {
			const sections = document.querySelectorAll('.help-section');
			let current = activeSection;
			const scrollTop = scrollContainer.scrollTop;

			sections.forEach((section) => {
				const sectionTop = (section as HTMLElement).offsetTop;
				if (scrollTop >= sectionTop - 100) {
					current = section.id;
				}
			});

			setActiveSection(current);

			// Show back to top button after scrolling down 300px
			setShowBackToTop(scrollTop > 300);
		};

		scrollContainer.addEventListener('scroll', handleScroll);
		return () => scrollContainer.removeEventListener('scroll', handleScroll);
	}, [activeSection]);

	const scrollToTop = () => {
		const scrollContainer = document.querySelector('.dashboard-content');
		if (scrollContainer) {
			scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const tocSections = [
		{ id: 'dashboard-widgets', label: 'Dashboard Widgets', icon: 'pi-th-large' },
		{ id: 'insights-generation', label: 'How Insights Are Generated', icon: 'pi-lightbulb' },
		{ id: 'interacting-insights', label: 'Interacting with Insights', icon: 'pi-comments' },
		{ id: 'notifications', label: 'Notifications System', icon: 'pi-bell' },
		{ id: 'recent-activity', label: 'Recent Activity', icon: 'pi-history' },
		{ id: 'lead-score', label: 'Lead Score Calculation', icon: 'pi-chart-line' },
		{ id: 'customer-visibility', label: 'Customers You Can See', icon: 'pi-eye' },
		{ id: 'subscriptions', label: 'Agent Subscriptions', icon: 'pi-credit-card' },
		{ id: 'gift-codes', label: 'Purchasing Gift Codes', icon: 'pi-gift' },
		{ id: 'navigation', label: 'Quick Actions & Navigation', icon: 'pi-compass' },
	];

	if (isMasterAgent) {
		tocSections.push({ id: 'master-agent', label: 'Master Agent Features', icon: 'pi-shield' });
	}

	// Search keywords for each section
	const sectionKeywords: Record<string, string[]> = {
		'dashboard-widgets': ['dashboard', 'widgets', 'metrics', 'customers', 'insights', 'conversions', 'notifications', 'urgent', 'follow-up', 'cards'],
		'insights-generation': ['insights', 'generated', 'types', 'payment stress', 'refinance', 'calculator', 'rate alert', 'first time buyer', 'priority', 'urgent', 'high', 'medium', 'low'],
		'interacting-insights': ['insights', 'lifecycle', 'status', 'new', 'viewed', 'contacted', 'in progress', 'converted', 'edit', 'priority', 'lead score', 'action', 'call', 'email'],
		'notifications': ['notifications', 'alerts', 'bell', 'unread', 'agent insights', 'customer updates', 'system'],
		'customer-visibility': ['privacy', 'consent', 'sharing', 'visible', 'missing customer', 'cannot see', 'permissions', 'assigned', 'opted out', 'add customer'],
		'recent-activity': ['activity', 'history', 'timeline', 'customer added', 'insight viewed', 'contacted', 'status changed', 'resolved'],
		'lead-score': ['lead score', 'scoring', 'calculator usage', 'assessment', 'engagement', 'financial profile', 'priority', 'hot lead', 'warm lead', 'cold lead'],
		'subscriptions': ['subscription', 'plan', 'billing', 'payment', 'stripe', 'manage', 'pro', 'professional'],
		'gift-codes': ['gift', 'codes', 'purchase', 'premium', 'redeem', 'customer', 'EWXG', 'share'],
		'navigation': ['navigation', 'sidebar', 'menu', 'quick actions', 'add customer', 'search', 'filter', 'profile'],
		'master-agent': ['master agent', 'super agent', 'manage agents', 'invite', 'customer assignments', 'system settings', 'limits', 'subscription limits', 'calculator limits', 'insight limits', 'invite', 'invitation', 'resend', 'pending', 'registration', 'password', 'expired link'],
	};

	// Filter TOC sections based on search
	const filteredTocSections = useMemo(() => {
		if (!searchQuery.trim()) return tocSections;
		const query = searchQuery.toLowerCase();
		return tocSections.filter(section => {
			const keywords = sectionKeywords[section.id] || [];
			return section.label.toLowerCase().includes(query) ||
				keywords.some(keyword => keyword.toLowerCase().includes(query));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery, tocSections]);

	return (
		<div className="portal-guide-page">
			{/* Table of Contents Sidebar */}
			<div className="guide-toc">
				<h3>Portal Guide</h3>
				<div className="guide-search">
					<InputText
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search guide..."
						className="guide-search-input"
					/>
					{searchQuery ? (
						<button
							className="search-clear-btn"
							onClick={() => setSearchQuery('')}
							title="Clear search"
						>
							<i className="pi pi-times" />
						</button>
					) : (
						<i className="pi pi-search search-icon" />
					)}
				</div>
				<nav className="toc-nav">
					{filteredTocSections.length === 0 ? (
						<div className="no-results">
							<i className="pi pi-search" />
							<p>No sections found for "{searchQuery}"</p>
						</div>
					) : (
						filteredTocSections.map((section) => (
							<button
								key={section.id}
								className={`toc-item ${activeSection === section.id ? 'active' : ''}`}
								onClick={() => scrollToSection(section.id)}
							>
								<i className={`pi ${section.icon}`}></i>
								<span>{section.label}</span>
							</button>
						))
					)}
				</nav>
			</div>

			{/* Main Content */}
			<div className="guide-content">
				<div className="guide-header">
					<h1>Agent Portal Guide</h1>
					<p>A comprehensive guide to understanding and using the EquityWorkx Agent Portal</p>
				</div>

				{/* Section 1: Dashboard Widgets */}
				<section id="dashboard-widgets" className="help-section">
					<Card>
						<h2><i className="pi pi-th-large"></i> Dashboard Portal Widgets</h2>
						<p className="section-intro">
							The dashboard is your command center, providing a real-time overview of your customers, insights, and activities.
							Here's what each widget tells you:
						</p>

						{/* Metric Cards Example */}
						<div className="help-example">
							<h3>Key Metrics</h3>
							<div className="help-screenshot">
								<div className="mock-metrics-grid">
									<div className="mock-metric-card">
										<div className="mock-metric-icon customers-icon">
											<i className="pi pi-users"></i>
										</div>
										<div className="mock-metric-details">
											<h3>24</h3>
											<p>Total Customers</p>
										</div>
									</div>

									<div className="mock-metric-card">
										<div className="mock-metric-icon insights-icon">
											<i className="pi pi-exclamation-triangle"></i>
										</div>
										<div className="mock-metric-details">
											<h3>12</h3>
											<p>Active Insights</p>
											<span className="mock-urgent-badge">3 urgent</span>
										</div>
									</div>

									<div className="mock-metric-card">
										<div className="mock-metric-icon conversions-icon">
											<i className="pi pi-check-circle"></i>
										</div>
										<div className="mock-metric-details">
											<h3>5</h3>
											<p>Conversions This Week</p>
										</div>
									</div>

									<div className="mock-metric-card clickable">
										<div className="mock-metric-icon notifications-icon">
											<i className="pi pi-bell"></i>
										</div>
										<div className="mock-metric-details">
											<h3>7</h3>
											<p>Unread Notifications</p>
										</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-users" style={{ color: '#3498db' }}></i> Total Customers:</strong> Shows the total number of customers assigned to you.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-exclamation-triangle" style={{ color: '#e74c3c' }}></i> Active Insights:</strong> Insights that need your attention. Red badge indicates urgent items requiring immediate action.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-check-circle" style={{ color: '#2ecc71' }}></i> Conversions This Week:</strong> Number of insights successfully converted in the last 7 days.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-bell" style={{ color: '#9b59b6' }}></i> Unread Notifications:</strong> Click this card to view unread notifications. Updates in real-time.
								</div>
							</div>
						</div>

						{/* Action Panels Example */}
						<div className="help-example">
							<h3>Action Panels</h3>
							<div className="help-screenshot">
								<div className="mock-action-panels">
									{/* Urgent Actions */}
									<div className="mock-panel urgent-panel">
										<div className="mock-panel-header">
											<i className="pi pi-exclamation-circle"></i>
											<h4>Urgent Actions</h4>
											<span className="mock-count-badge">3</span>
										</div>
										<div className="mock-panel-content">
											<div className="mock-action-item">
												<div className="mock-action-info">
													<div className="mock-action-title">High Payment Stress Detected</div>
													<div className="mock-action-customer">Sarah Johnson</div>
												</div>
												<button className="mock-btn-danger">Call Now</button>
											</div>
										</div>
									</div>

									{/* Follow-ups Due */}
									<div className="mock-panel followup-panel">
										<div className="mock-panel-header">
											<i className="pi pi-clock"></i>
											<h4>Follow-ups Due</h4>
											<span className="mock-count-badge">5</span>
										</div>
										<div className="mock-panel-content">
											<div className="mock-action-item">
												<div className="mock-action-info">
													<div className="mock-action-title">Refinance Opportunity</div>
													<div className="mock-action-customer">
														Mike Chen
														<span className="mock-expiry">2 days left</span>
													</div>
												</div>
												<button className="mock-btn-warning">Follow Up</button>
											</div>
										</div>
									</div>

									{/* New Insights */}
									<div className="mock-panel new-panel">
										<div className="mock-panel-header">
											<i className="pi pi-eye-slash"></i>
											<h4>New Insights</h4>
											<span className="mock-count-badge">4</span>
										</div>
										<div className="mock-panel-content">
											<div className="mock-action-item">
												<div className="mock-action-info">
													<span className="mock-new-badge">NEW</span>
													<div className="mock-action-title">Calculator Engagement</div>
													<div className="mock-action-customer">Amanda Lee</div>
												</div>
												<span className="mock-priority-badge high">HIGH</span>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-exclamation-circle" style={{ color: '#e74c3c' }}></i> Urgent Actions:</strong> Highest priority items requiring immediate attention (contact within 24 hours).
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-clock" style={{ color: '#f39c12' }}></i> Follow-ups Due:</strong> Insights expiring in the next 3 days or marked as "in progress" that need follow-up.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-eye-slash" style={{ color: '#3498db' }}></i> New Insights:</strong> Unviewed insights awaiting your review. The "NEW" badge indicates you haven't opened them yet.
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Quick Tips</h4>
							<ul>
								<li>Check the dashboard first thing each morning to prioritize your day</li>
								<li>Always address urgent actions before moving to follow-ups</li>
								<li>The notification card is clickable - it takes you directly to your notification center</li>
								<li>Metrics update in real-time as you work with customers and insights</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 2: How Insights Are Generated */}
				<section id="insights-generation" className="help-section">
					<Card>
						<h2><i className="pi pi-lightbulb"></i> How Insights Are Generated</h2>
						<p className="section-intro">
							The EquityWorkx system continuously analyzes customer behavior in the mobile app to identify opportunities
							and generate actionable insights for agents.
						</p>

						<div className="help-example">
							<h3>Insight Types</h3>
							<div className="insight-types-grid">
								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#e74c3c' }}>
										<i className="pi pi-exclamation-triangle"></i>
									</div>
									<h4>Payment Stress</h4>
									<p>No mortgage payment has posted for noticeably longer than the customer's usual cycle. Approach gently - it is an inference from transaction timing, not a confirmed missed payment.</p>
									<span className="priority-label urgent">URGENT</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#3498db' }}>
										<i className="pi pi-refresh"></i>
									</div>
									<h4>Refinance Opportunity</h4>
									<p>The customer's rate sits meaningfully above the current market rate, with the monthly saving worth quoting.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#f39c12' }}>
										<i className="pi pi-chart-bar"></i>
									</div>
									<h4>Calculator Engagement</h4>
									<p>The customer has run the same calculator repeatedly - a sign they are working a real number rather than browsing.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#9b59b6' }}>
										<i className="pi pi-percentage"></i>
									</div>
									<h4>Rate Alert</h4>
									<p>A rate the customer asked to be watched has been reached.</p>
									<span className="priority-label medium">MEDIUM</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#2ecc71' }}>
										<i className="pi pi-home"></i>
									</div>
									<h4>First Time Buyer</h4>
									<p>No mortgage on file, but repeated use of the home-buying calculators. Early in the process, and usually before a lender has been chosen.</p>
									<span className="priority-label medium">MEDIUM</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#1abc9c' }}>
										<i className="pi pi-dollar"></i>
									</div>
									<h4>High Value Prospect</h4>
									<p>An unusually high engagement score. Based on how much the customer is using the app, not on income or assets - the app does not collect those.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#16a085' }}>
										<i className="pi pi-check-square"></i>
									</div>
									<h4>Pre-Approval Ready</h4>
									<p>The customer's intent score has crossed the pre-approval threshold, based on the actions they have taken in the app.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#95a5a6' }}>
										<i className="pi pi-user-minus"></i>
									</div>
									<h4>Inactive User</h4>
									<p>A customer who used the app meaningfully and then went quiet. Someone who never engaged in the first place will not appear here.</p>
									<span className="priority-label low">LOW</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#27ae60' }}>
										<i className="pi pi-home"></i>
									</div>
									<h4>Home Equity Opportunity</h4>
									<p>The customer has built substantial equity at a loan-to-value lenders will work with. The insight carries the equity figure and LTV.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#c0392b' }}>
										<i className="pi pi-credit-card"></i>
									</div>
									<h4>Debt Consolidation</h4>
									<p>Revolving balances that the customer's home equity could actually absorb. Only raised when the equity covers the debt - otherwise there is nothing to offer.</p>
									<span className="priority-label medium">MEDIUM</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#8e44ad' }}>
										<i className="pi pi-arrow-down"></i>
									</div>
									<h4>Market Timing</h4>
									<p>Rates have fallen recently <em>and</em> the customer is paying above market. Both have to be true, so this is quieter than a general rate move.</p>
									<span className="priority-label high">HIGH</span>
								</div>

								<div className="insight-type-card">
									<div className="insight-type-icon" style={{ background: '#d35400' }}>
										<i className="pi pi-flag"></i>
									</div>
									<h4>Financial Milestone</h4>
									<p>The customer has finished a savings or payoff goal, or passed the halfway mark. A reason to make contact that is not a pitch.</p>
									<span className="priority-label medium">MEDIUM</span>
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Priority Levels</h3>
							<div className="priority-explanation">
								<div className="priority-item">
									<span className="mock-priority-badge urgent">URGENT</span>
									<div className="priority-desc">
										<strong>Immediate Action Required:</strong> Customer situation requires attention within 24 hours.
										These often indicate financial stress or time-sensitive opportunities.
									</div>
								</div>
								<div className="priority-item">
									<span className="mock-priority-badge high">HIGH</span>
									<div className="priority-desc">
										<strong>High Priority:</strong> Strong lead or opportunity. Should be contacted within 2-3 days.
										High conversion potential.
									</div>
								</div>
								<div className="priority-item">
									<span className="mock-priority-badge medium">MEDIUM</span>
									<div className="priority-desc">
										<strong>Standard Priority:</strong> Normal follow-up timeline. Contact within 5-7 days.
										Good opportunity but not time-critical.
									</div>
								</div>
								<div className="priority-item">
									<span className="mock-priority-badge low">LOW</span>
									<div className="priority-desc">
										<strong>Low Priority:</strong> Informational or long-term nurturing. Follow up as time permits.
									</div>
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> How It Works</h4>
							<ul>
								<li>Insights are generated automatically based on customer app activity and financial data</li>
								<li>Cloud functions analyze behavior patterns, calculator usage, and assessment completions</li>
								<li>Each insight is assigned a priority level based on urgency and opportunity value</li>
								<li>Lead scores are calculated to help you prioritize your outreach efforts</li>
								<li>Insights have expiration dates to ensure timely follow-up</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 3: Interacting with Insights */}
				<section id="interacting-insights" className="help-section">
					<Card>
						<h2><i className="pi pi-comments"></i> Interacting with Insights</h2>
						<p className="section-intro">
							Insights move through a lifecycle as you work with them. Understanding this workflow helps you
							manage your customer relationships effectively.
						</p>

						<div className="help-example">
							<h3>What an insight tells you</h3>
							<p>
								Opening an insight shows a brief assembled from what the customer has actually done, rather than
								a title and a status. Sections only appear when there is something to put in them.
							</p>
							<ul>
								<li>
									<strong>Why this lead</strong> &mdash; what triggered it, and the numbers behind it: intent or
									engagement score, which calculator they were using and how often, how ready they appear to be.
								</li>
								<li>
									<strong>Suggested next steps</strong> &mdash; concrete actions for this insight type. A starting
									point, not a script.
								</li>
								<li>
									<strong>How to reach them</strong> &mdash; preferred contact method, best time to contact, and
									phone number where the customer has provided one.
								</li>
								<li>
									<strong>Financial snapshot</strong> &mdash; mortgage balance, rate, monthly payment, lender,
									estimated equity and loan-to-value, drawn from their connected accounts. Absent for customers
									with no mortgage linked.
								</li>
							</ul>
							<div className="help-tip">
								<strong><i className="pi pi-info-circle"></i> Figures come from the customer's own data.</strong> Balances
								and values update from their linked accounts, and may reflect corrections the customer has made
								themselves. Treat them as a well-informed starting point for the conversation, not as verified
								underwriting figures.
							</div>
						</div>

						<div className="help-example">
							<h3>Insight Lifecycle</h3>
							<div className="lifecycle-diagram">
								<div className="lifecycle-step">
									<div className="lifecycle-icon new">
										<i className="pi pi-plus-circle"></i>
									</div>
									<h4>New</h4>
									<p>Insight just created</p>
								</div>
								<i className="pi pi-arrow-right lifecycle-arrow"></i>
								<div className="lifecycle-step">
									<div className="lifecycle-icon viewed">
										<i className="pi pi-eye"></i>
									</div>
									<h4>Viewed</h4>
									<p>You've reviewed it</p>
								</div>
								<i className="pi pi-arrow-right lifecycle-arrow"></i>
								<div className="lifecycle-step">
									<div className="lifecycle-icon contacted">
										<i className="pi pi-phone"></i>
									</div>
									<h4>Contacted</h4>
									<p>Initial contact made</p>
								</div>
								<i className="pi pi-arrow-right lifecycle-arrow"></i>
								<div className="lifecycle-step">
									<div className="lifecycle-icon progress">
										<i className="pi pi-sync"></i>
									</div>
									<h4>In Progress</h4>
									<p>Actively working</p>
								</div>
								<i className="pi pi-arrow-right lifecycle-arrow"></i>
								<div className="lifecycle-step">
									<div className="lifecycle-icon converted">
										<i className="pi pi-check-circle"></i>
									</div>
									<h4>Converted</h4>
									<p>Successfully closed</p>
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Action Buttons</h3>
							<div className="help-screenshot">
								<div className="mock-insight-detail">
									<div className="mock-insight-header">
										<h3>Refinance Opportunity</h3>
										<span className="mock-priority-badge high">HIGH</span>
									</div>
									<div className="mock-insight-body">
										<p><strong>Customer:</strong> John Smith</p>
										<p><strong>Lead Score:</strong> 78</p>
										<p><strong>Description:</strong> Customer could save $450/month by refinancing at current rates.</p>
									</div>
									<div className="mock-insight-actions">
										<button className="mock-btn-view">
											<i className="pi pi-eye"></i> View
										</button>
										<button className="mock-btn-call">
											<i className="pi pi-phone"></i> Call
										</button>
										<button className="mock-btn-edit">
											<i className="pi pi-pencil"></i> Edit
										</button>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-eye" style={{ color: '#3498db' }}></i> View Button:</strong> Opens full insight details and automatically marks the insight as "viewed"
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-phone" style={{ color: '#2ecc71' }}></i> Action Button:</strong> Record that you've taken action on this insight (call, email, etc.)
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-pencil" style={{ color: '#f39c12' }}></i> Edit Button:</strong> Opens the insight editor to update priority, lead score, and status
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Editing Insight Details</h3>
							<p>When you click the Edit button on an insight, you can modify key attributes to reflect your assessment of the opportunity:</p>
							<div className="help-screenshot">
								<div className="mock-edit-dialog">
									<div className="mock-dialog-header">
										<h4>Edit Insight</h4>
									</div>
									<div className="mock-dialog-body">
										<div className="mock-form-field">
											<label>Priority</label>
											<select className="mock-select">
												<option>Urgent</option>
												<option selected>High</option>
												<option>Medium</option>
												<option>Low</option>
											</select>
										</div>
										<div className="mock-form-field">
											<label>Lead Score (0-100)</label>
											<div className="mock-number-input">
												<button>-</button>
												<span>78</span>
												<button>+</button>
											</div>
										</div>
										<div className="mock-form-field">
											<label>Status</label>
											<select className="mock-select">
												<option>New</option>
												<option>Viewed</option>
												<option selected>Contacted</option>
												<option>In Progress</option>
												<option>Converted</option>
												<option>Dismissed</option>
											</select>
										</div>
									</div>
									<div className="mock-dialog-footer">
										<button className="mock-btn-ghost">Cancel</button>
										<button className="mock-btn-primary">Save Changes</button>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-flag" style={{ color: '#e74c3c' }}></i> Priority:</strong> Adjust based on your assessment. Upgrade to "Urgent" if customer expresses immediate need, or downgrade if opportunity is less pressing than initially indicated.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-chart-line" style={{ color: '#3498db' }}></i> Lead Score:</strong> Manually adjust the lead score (0-100) based on your conversations. Increase if customer shows strong interest, decrease if they're not ready to move forward.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-sync" style={{ color: '#9b59b6' }}></i> Status:</strong> Update to reflect where you are in the sales process. This helps track progress and ensures proper follow-up.
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Best Practices</h4>
							<ul>
								<li><strong>Review daily:</strong> Check new insights every morning and prioritize by urgency</li>
								<li><strong>Act on urgent items:</strong> Contact customers with urgent insights within 24 hours</li>
								<li><strong>Update status:</strong> Always update insight status after contact to track progress</li>
								<li><strong>Add notes:</strong> Document your conversations in the customer detail page</li>
								<li><strong>Follow up:</strong> Set reminders for follow-ups before insights expire</li>
								<li><strong>Use contact suggestions:</strong> Each insight includes recommended talking points</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 4: Notifications System */}
				<section id="notifications" className="help-section">
					<Card>
						<h2><i className="pi pi-bell"></i> Notifications System</h2>
						<p className="section-intro">
							Stay informed about important events, new insights, and customer updates through the notification system.
						</p>

						<div className="help-example">
							<h3>Notification Panel</h3>
							<div className="help-screenshot">
								<div className="mock-notification-panel">
									<div className="mock-notification-header">
										<h4>Notifications</h4>
										<button className="mock-view-all-btn">View All</button>
									</div>
									<div className="mock-notification-list">
										<div className="mock-notification-item unread">
											<div className="mock-notif-icon agent-insight">
												<i className="pi pi-chart-line"></i>
											</div>
											<div className="mock-notif-content">
												<div className="mock-notif-title">New High-Priority Insight</div>
												<div className="mock-notif-message">Sarah Johnson - Calculator Engagement</div>
												<div className="mock-notif-time">5 minutes ago</div>
											</div>
											<div className="mock-unread-badge"></div>
										</div>

										<div className="mock-notification-item">
											<div className="mock-notif-icon customer-update">
												<i className="pi pi-user"></i>
											</div>
											<div className="mock-notif-content">
												<div className="mock-notif-title">Customer Assessment Completed</div>
												<div className="mock-notif-message">Mike Chen completed their financial profile</div>
												<div className="mock-notif-time">2 hours ago</div>
											</div>
										</div>

										<div className="mock-notification-item">
											<div className="mock-notif-icon system">
												<i className="pi pi-cog"></i>
											</div>
											<div className="mock-notif-content">
												<div className="mock-notif-title">System Update</div>
												<div className="mock-notif-message">New features available in the portal</div>
												<div className="mock-notif-time">1 day ago</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-bell" style={{ color: '#9b59b6' }}></i> Accessing Notifications:</strong> Access from bell icon in top right. Badge shows unread count.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-circle-fill" style={{ color: '#3498db' }}></i> Unread Indicator:</strong> Blue dot indicates unread. Click notification to mark as read and navigate to related content.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-palette" style={{ color: '#95a5a6' }}></i> Icon Colors:</strong> Blue (insights), Green (customers), Gray (system notifications).
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Notification Types</h3>
							<div className="notification-types">
								<div className="notif-type-item">
									<i className="pi pi-chart-line" style={{ color: '#3498db' }}></i>
									<div>
										<strong>Agent Insights:</strong> New insights generated, high-priority alerts, insights expiring soon
									</div>
								</div>
								<div className="notif-type-item">
									<i className="pi pi-user" style={{ color: '#2ecc71' }}></i>
									<div>
										<strong>Customer Updates:</strong> Assessment completed, profile updated, new customer assigned
									</div>
								</div>
								<div className="notif-type-item">
									<i className="pi pi-cog" style={{ color: '#95a5a6' }}></i>
									<div>
										<strong>System:</strong> Portal updates, maintenance notices, feature announcements
									</div>
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Managing Notifications</h4>
							<ul>
								<li>Notifications update in real-time - no need to refresh the page</li>
								<li>Click on a notification to mark it as read and navigate to the relevant page</li>
								<li>Customize your notification preferences in your Profile settings</li>
								<li>Important notifications (urgent insights) cannot be disabled</li>
								<li>The bell icon badge shows your total unread count</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 5: Recent Activity */}
				<section id="recent-activity" className="help-section">
					<Card>
						<h2><i className="pi pi-history"></i> Recent Activity</h2>
						<p className="section-intro">
							Track all your interactions and system events in one place. Activity history helps you understand
							customer engagement and your work patterns.
						</p>

						<div className="help-example">
							<h3>Activity Timeline</h3>
							<div className="help-screenshot">
								<div className="mock-activity-panel">
									<div className="mock-activity-header">
										<h4>Recent Activity</h4>
										<button className="mock-view-all-btn">View All</button>
									</div>
									<div className="mock-activity-timeline">
										<div className="mock-activity-item">
											<div className="mock-activity-icon" style={{ background: '#3498db' }}>
												<i className="pi pi-eye"></i>
											</div>
											<div className="mock-activity-content">
												<div className="mock-activity-title">Insight Viewed</div>
												<div className="mock-activity-desc">Reviewed refinance opportunity for Sarah Johnson</div>
												<div className="mock-activity-time">Today at 2:30 PM</div>
											</div>
										</div>

										<div className="mock-activity-item">
											<div className="mock-activity-icon" style={{ background: '#2ecc71' }}>
												<i className="pi pi-user-plus"></i>
											</div>
											<div className="mock-activity-content">
												<div className="mock-activity-title">Customer Added</div>
												<div className="mock-activity-desc">Added new customer: Mike Chen</div>
												<div className="mock-activity-time">Today at 11:15 AM</div>
											</div>
										</div>

										<div className="mock-activity-item">
											<div className="mock-activity-icon" style={{ background: '#f39c12' }}>
												<i className="pi pi-phone"></i>
											</div>
											<div className="mock-activity-content">
												<div className="mock-activity-title">Customer Contacted</div>
												<div className="mock-activity-desc">Called Amanda Lee regarding calculator engagement</div>
												<div className="mock-activity-time">Yesterday at 4:45 PM</div>
											</div>
										</div>

										<div className="mock-activity-item">
											<div className="mock-activity-icon" style={{ background: '#9b59b6' }}>
												<i className="pi pi-check-circle"></i>
											</div>
											<div className="mock-activity-content">
												<div className="mock-activity-title">Insight Resolved</div>
												<div className="mock-activity-desc">Converted refinance opportunity for John Smith</div>
												<div className="mock-activity-time">Yesterday at 10:20 AM</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-history" style={{ color: '#16a085' }}></i> Accessing Activity:</strong> Access from history icon in top right or Recent Activity panel on dashboard.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-arrow-circle-right" style={{ color: '#3498db' }}></i> Navigation:</strong> Click any activity item to navigate to the related customer or insight.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-palette" style={{ color: '#95a5a6' }}></i> Color Coding:</strong> Blue (views), Green (additions), Orange (contacts), Purple (conversions).
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Activity Types</h3>
							<div className="activity-types-grid">
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#2ecc71' }}>
										<i className="pi pi-user-plus"></i>
									</div>
									<strong>Customer Added</strong>
									<p>New customer created in system</p>
								</div>
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#3498db' }}>
										<i className="pi pi-eye"></i>
									</div>
									<strong>Insight Viewed</strong>
									<p>You reviewed an insight</p>
								</div>
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#f39c12' }}>
										<i className="pi pi-phone"></i>
									</div>
									<strong>Customer Contacted</strong>
									<p>Outreach made to customer</p>
								</div>
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#e74c3c' }}>
										<i className="pi pi-pencil"></i>
									</div>
									<strong>Status Changed</strong>
									<p>Insight status updated</p>
								</div>
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#9b59b6' }}>
										<i className="pi pi-check-circle"></i>
									</div>
									<strong>Insight Resolved</strong>
									<p>Successfully converted or closed</p>
								</div>
								<div className="activity-type">
									<div className="activity-type-icon" style={{ background: '#16a085' }}>
										<i className="pi pi-user-edit"></i>
									</div>
									<strong>Customer Updated</strong>
									<p>Customer information modified</p>
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Using Activity History</h4>
							<ul>
								<li>All your actions are automatically logged - no manual tracking needed</li>
								<li>Click any activity item to jump to the related customer or insight</li>
								<li>Use activity history to recall recent conversations before follow-ups</li>
								<li>Activity timestamps help you track response times and follow-up schedules</li>
								<li>View full activity history on the dedicated Activities page</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 6: Lead Score Calculation */}
				<section id="lead-score" className="help-section">
					<Card>
						<h2><i className="pi pi-chart-line"></i> Lead Score Calculation</h2>
						<p className="section-intro">
							Lead scores help you prioritize which customers to contact first. Scores range from 0-100 and are
							calculated based on multiple engagement and financial indicators.
						</p>

						<div className="help-example">
							<h3>Score Breakdown</h3>
							<div className="help-screenshot">
								<div className="mock-score-visual">
									<div className="score-gauge">
										<div className="gauge-chart">
											<div className="gauge-fill" style={{ width: '78%' }}></div>
											<div className="gauge-marker" style={{ left: '78%' }}></div>
										</div>
										<div className="gauge-labels">
											<span>0</span>
											<span>40</span>
											<span>70</span>
											<span>100</span>
										</div>
										<div className="gauge-score">78</div>
										<div className="gauge-label">High Priority Lead</div>
									</div>

									<div className="score-factors">
										<h4>How the score is built:</h4>
										<div className="score-factor">
											<div className="factor-label">
												<i className="pi pi-chart-line"></i>
												<span>Engagement score &times; 0.3</span>
											</div>
											<div className="factor-bar">
												<div className="factor-fill" style={{ width: '30%', background: '#2ecc71' }}></div>
											</div>
											<span className="factor-value">up to 30</span>
										</div>
										<div className="score-factor">
											<div className="factor-label">
												<i className="pi pi-forward"></i>
												<span>Intent score &times; 0.3</span>
											</div>
											<div className="factor-bar">
												<div className="factor-fill" style={{ width: '30%', background: '#2ecc71' }}></div>
											</div>
											<span className="factor-value">up to 30</span>
										</div>
										<div className="score-factor">
											<div className="factor-label">
												<i className="pi pi-clock"></i>
												<span>Recency &mdash; full credit today, none after 20 days</span>
											</div>
											<div className="factor-bar">
												<div className="factor-fill" style={{ width: '20%', background: '#3498db' }}></div>
											</div>
											<span className="factor-value">up to 20</span>
										</div>
										<div className="score-factor">
											<div className="factor-label">
												<i className="pi pi-dollar"></i>
												<span>Refinance saving, where one applies</span>
											</div>
											<div className="factor-bar">
												<div className="factor-fill" style={{ width: '20%', background: '#3498db' }}></div>
											</div>
											<span className="factor-value">up to 20</span>
										</div>
									</div>
								</div>

								<div className="help-tip">
									<strong><i className="pi pi-info-circle"></i> Income and credit score are not part of this.</strong> The
									app does not collect them, so nothing in the score depends on them. It is built from what the
									customer actually does in the app and how recently they did it.
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Engagement fades when someone goes quiet</h3>
							<p>
								Engagement score is the largest single input, and it does not hold still. It starts at a neutral
								50, rises with activity, and falls back when a customer stops using the app &mdash; roughly five
								points a month after a two-week grace period.
							</p>
							<p>
								In practice a well-engaged customer stays in your working list for about six months of silence
								before dropping out of it, while someone who was never really engaged drops out within a couple
								of months. If a lead score looks lower than you remember, this is usually why.
							</p>
						</div>

						<div className="help-example">
							<h3>Score Ranges</h3>
							<div className="score-ranges">
								<div className="score-range high-score">
									<div className="range-header">
										<span className="range-value">71-100</span>
										<span className="range-badge high">HIGH PRIORITY</span>
									</div>
									<p><strong>Hot Lead:</strong> Customer is highly engaged and ready for contact. These leads show strong purchase intent or refinance readiness. Contact within 24-48 hours.</p>
									<ul>
										<li>Heavy calculator usage</li>
										<li>Repeated mortgage views</li>
										<li>Active in the app within the last few days</li>
									</ul>
								</div>

								<div className="score-range medium-score">
									<div className="range-header">
										<span className="range-value">41-70</span>
										<span className="range-badge medium">MEDIUM PRIORITY</span>
									</div>
									<p><strong>Warm Lead:</strong> Customer shows moderate interest and engagement. Good opportunity but not urgent. Contact within 3-5 days.</p>
									<ul>
										<li>Some app activity</li>
										<li>Occasional engagement</li>
										<li>Or previously engaged and beginning to go quiet</li>
									</ul>
								</div>

								<div className="score-range low-score">
									<div className="range-header">
										<span className="range-value">0-40</span>
										<span className="range-badge low">LOW PRIORITY</span>
									</div>
									<p><strong>Cold Lead:</strong> Limited engagement or early-stage interest. Focus on nurturing and education. Contact as time permits or when activity increases.</p>
									<ul>
										<li>Minimal app usage</li>
										<li>Incomplete profile</li>
										<li>Low engagement signals</li>
									</ul>
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Using Lead Scores</h4>
							<ul>
								<li>Scores update in real-time as customers engage with the app</li>
								<li>Prioritize high-scoring leads (71+) for immediate outreach</li>
								<li>Medium scores (41-70) are good for regular follow-up cycles</li>
								<li>Low scores (0-40) benefit from nurture campaigns and education</li>
								<li>Combine lead score with insight priority for optimal prioritization</li>
								<li>A low score with an urgent insight still requires immediate attention</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Customers You Can See */}
				<section id="customer-visibility" className="help-section">
					<Card>
						<h2><i className="pi pi-eye"></i> Customers You Can See</h2>
						<p className="section-intro">
							Customers control who inside EquityWorkx may see their financial information. If someone you
							expect to find is missing, this is usually the reason rather than a fault.
						</p>

						<div className="help-example">
							<h3>The three settings</h3>
							<ul>
								<li>
									<strong>Share with any agent</strong> &mdash; the default. Visible to every active agent.
								</li>
								<li>
									<strong>Share with my agent only</strong> &mdash; visible solely to the agent they are assigned
									to. This holds even for master and super agents: a senior role is not the same as consent.
								</li>
								<li>
									<strong>Do not share</strong> &mdash; not visible to any agent, including their own, and no
									insights are generated for them.
								</li>
							</ul>
							<div className="help-tip">
								<strong><i className="pi pi-info-circle"></i> A withheld customer is simply absent.</strong> They
								do not appear in your list, your counts, or search, and their insights are not shown. You will not
								see a locked or greyed-out row, because the fact that the record exists is itself something the
								customer has chosen not to share.
							</div>
						</div>

						<div className="help-example">
							<h3>Working through your list</h3>
							<ul>
								<li>
									The customer list loads a page at a time. When more remain, a <strong>Load more</strong> button
									appears with a count of what is currently shown.
								</li>
								<li>
									Search and the status filter run across your whole book, not only the page in front of you, so
									use them rather than scrolling to find someone specific.
								</li>
								<li>
									Lead score and tag filters apply to what has been loaded so far. Load more, or narrow with
									search first, if you are hunting for someone in particular.
								</li>
								<li>
									Adding a customer by hand assigns them to you and they appear in your list immediately.
								</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 7: Agent Subscriptions */}
				<section id="subscriptions" className="help-section">
					<Card>
						<h2><i className="pi pi-credit-card"></i> Agent Subscriptions</h2>
						<p className="section-intro">
							Manage your subscription plan and purchase gift codes for your customers. Your subscription determines your access level and the features available to you.
						</p>

						<div className="help-example">
							<h3>Your Subscription</h3>
							<p>Access your subscription details from the Subscription page in the navigation menu.</p>
							<div className="help-screenshot">
								<div className="mock-subscription-card">
									<div className="mock-sub-header">
										<div className="mock-sub-plan">
											<span className="mock-plan-badge pro">PRO</span>
											<h4>Professional Plan</h4>
										</div>
										<span className="mock-sub-status active">Active</span>
									</div>
									<div className="mock-sub-details">
										<div className="mock-sub-item">
											<i className="pi pi-calendar"></i>
											<span>Next billing: January 15, 2025</span>
										</div>
										<div className="mock-sub-item">
											<i className="pi pi-dollar"></i>
											<span>$49.99/month</span>
										</div>
									</div>
									<div className="mock-sub-actions">
										<button className="mock-btn-outline">Manage Subscription</button>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-check-circle" style={{ color: '#2ecc71' }}></i> Active Status:</strong> Your subscription is current and all features are available.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-credit-card" style={{ color: '#3498db' }}></i> Manage Subscription:</strong> Click to update payment method, change plans, or view billing history through the Stripe customer portal.
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Subscription Tips</h4>
							<ul>
								<li>Keep your subscription active to maintain access to all portal features</li>
								<li>Contact support if you need to change your plan or have billing questions</li>
								<li>You can view your billing history and update payment methods through the Stripe portal</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 8: Purchasing Gift Codes */}
				<section id="gift-codes" className="help-section">
					<Card>
						<h2><i className="pi pi-gift"></i> Purchasing Gift Codes</h2>
						<p className="section-intro">
							Purchase gift codes to give your customers premium access to the mobile app. Gift codes are a great way to provide value, build relationships, and incentivize referrals.
						</p>

						<div className="help-example">
							<h3>How Gift Codes Work</h3>
							<div className="gift-workflow">
								<div className="workflow-step">
									<div className="workflow-number">1</div>
									<div className="workflow-content">
										<strong>Purchase</strong>
										<p>Select a gift type and complete payment</p>
									</div>
								</div>
								<div className="workflow-arrow"><i className="pi pi-arrow-right"></i></div>
								<div className="workflow-step">
									<div className="workflow-number">2</div>
									<div className="workflow-content">
										<strong>Receive Code</strong>
										<p>Get unique code (EWXG-XXXX-XXXX-XXXX)</p>
									</div>
								</div>
								<div className="workflow-arrow"><i className="pi pi-arrow-right"></i></div>
								<div className="workflow-step">
									<div className="workflow-number">3</div>
									<div className="workflow-content">
										<strong>Share</strong>
										<p>Send to customer via email, text, or in person</p>
									</div>
								</div>
								<div className="workflow-arrow"><i className="pi pi-arrow-right"></i></div>
								<div className="workflow-step">
									<div className="workflow-number">4</div>
									<div className="workflow-content">
										<strong>Redeem</strong>
										<p>Customer activates in mobile app</p>
									</div>
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Purchasing a Gift Code</h3>
							<p>Navigate to the Gift Codes page from the sidebar to purchase gift codes for your customers.</p>
							<div className="help-screenshot">
								<div className="mock-gift-purchase">
									<div className="mock-gift-options">
										<div className="mock-gift-option selected">
											<div className="mock-gift-icon">
												<i className="pi pi-star"></i>
											</div>
											<div className="mock-gift-info">
												<h4>1 Month Premium</h4>
												<p>$9.99</p>
											</div>
										</div>
										<div className="mock-gift-option">
											<div className="mock-gift-icon">
												<i className="pi pi-star-fill"></i>
											</div>
											<div className="mock-gift-info">
												<h4>1 Year Premium</h4>
												<p>$99.99</p>
											</div>
										</div>
									</div>
									<button className="mock-btn-success">
										<i className="pi pi-shopping-cart"></i> Purchase Gift Code
									</button>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-tag" style={{ color: '#3498db' }}></i> Select Gift Type:</strong> Choose between monthly or annual premium access for your customer.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-user" style={{ color: '#2ecc71' }}></i> Link to Customer:</strong> Optionally select a customer to track who the gift is for (the code can still be given to anyone).
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-credit-card" style={{ color: '#9b59b6' }}></i> Secure Checkout:</strong> Payment is processed securely through Stripe. You'll be redirected to complete the purchase.
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Managing Gift Codes</h3>
							<p>View your purchased gift codes and their redemption status in your gift code history.</p>
							<div className="help-screenshot">
								<div className="mock-gift-history">
									<div className="mock-gift-row">
										<div className="mock-gift-code">EWXG-A1B2-C3D4-E5F6</div>
										<div className="mock-gift-type">1 Year Premium</div>
										<div className="mock-gift-status redeemed">Redeemed</div>
									</div>
									<div className="mock-gift-row">
										<div className="mock-gift-code">EWXG-X9Y8-Z7W6-V5U4</div>
										<div className="mock-gift-type">1 Month Premium</div>
										<div className="mock-gift-status available">Available</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-check-circle" style={{ color: '#2ecc71' }}></i> Available:</strong> Code has not been redeemed and can be shared with a customer.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-user" style={{ color: '#9b59b6' }}></i> Redeemed:</strong> Code has been activated by a customer in the mobile app.
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Gift Code Tips</h4>
							<ul>
								<li>Gift codes never expire until redeemed by a customer</li>
								<li>Use gift codes as incentives for hesitant customers or referral rewards</li>
								<li>Annual gift codes provide better value and longer customer engagement</li>
								<li>You can copy gift codes directly from your history to share easily</li>
								<li>Track which customers have redeemed their codes in your gift history</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 9: Quick Actions & Navigation */}
				<section id="navigation" className="help-section">
					<Card>
						<h2><i className="pi pi-compass"></i> Quick Actions & Navigation</h2>
						<p className="section-intro">
							Navigate efficiently through the portal and access common actions quickly.
						</p>

						<div className="help-example">
							<h3>Sidebar Navigation</h3>
							<div className="help-screenshot">
								<div className="mock-sidebar">
									<div className="mock-sidebar-header">
										<img src="/ew-color-logo_crop.png" alt="Logo" className="mock-logo" />
										<h4>Agent Portal</h4>
									</div>
									<div className="mock-sidebar-menu">
										<div className="mock-menu-item active">
											<i className="pi pi-home"></i>
											<span>Dashboard</span>
										</div>
										<div className="mock-menu-item">
											<i className="pi pi-users"></i>
											<span>Customers</span>
										</div>
										<div className="mock-menu-item">
											<i className="pi pi-chart-line"></i>
											<span>Insights</span>
										</div>
										<div className="mock-menu-item">
											<i className="pi pi-bell"></i>
											<span>Notifications</span>
										</div>
										<div className="mock-menu-item">
											<i className="pi pi-history"></i>
											<span>Recent Activity</span>
										</div>
										<div className="mock-menu-item">
											<i className="pi pi-user"></i>
											<span>Profile</span>
										</div>
										<div className="mock-menu-item highlight">
											<i className="pi pi-question-circle"></i>
											<span>Portal Guide</span>
										</div>
									</div>
								</div>
							</div>
							<div className="help-explanation">
								<div className="explanation-item">
									<strong><i className="pi pi-bars" style={{ color: '#3498db' }}></i> Main Navigation:</strong> Always visible on desktop; access via hamburger menu (☰) on mobile devices.
								</div>
								<div className="explanation-item">
									<strong><i className="pi pi-question-circle" style={{ color: '#f39c12' }}></i> Portal Guide:</strong> You are here! Access this guide anytime from the navigation menu.
								</div>
							</div>
						</div>

						<div className="help-example">
							<h3>Quick Actions</h3>
							<div className="quick-actions-list">
								<div className="quick-action-item">
									<i className="pi pi-plus-circle" style={{ color: '#3498db' }}></i>
									<div>
										<strong>Add Customer</strong>
										<p>Quickly add a new customer to your portfolio from the Dashboard</p>
									</div>
								</div>
								<div className="quick-action-item">
									<i className="pi pi-search" style={{ color: '#2ecc71' }}></i>
									<div>
										<strong>Search Customers</strong>
										<p>Find customers by name, email, or phone in the Customers page</p>
									</div>
								</div>
								<div className="quick-action-item">
									<i className="pi pi-filter" style={{ color: '#f39c12' }}></i>
									<div>
										<strong>Filter Insights</strong>
										<p>Filter by priority, status, or type on the Insights page</p>
									</div>
								</div>
								<div className="quick-action-item">
									<i className="pi pi-user-edit" style={{ color: '#9b59b6' }}></i>
									<div>
										<strong>Update Profile</strong>
										<p>Manage your profile, notification preferences, and MFA settings</p>
									</div>
								</div>
							</div>
						</div>

						<div className="help-tips">
							<h4><i className="pi pi-info-circle"></i> Navigation Tips</h4>
							<ul>
								<li>Use the hamburger menu (☰) on mobile to access the sidebar</li>
								<li>The active page is highlighted in the sidebar navigation</li>
								<li>Click the logo at the top to return to the Dashboard</li>
								<li>Use your browser's back button to navigate between pages</li>
								<li>The topbar shows your current page and provides quick access to notifications and activity</li>
							</ul>
						</div>
					</Card>
				</section>

				{/* Section 8: Master Agent Features (Conditional) */}
				{isMasterAgent && (
					<section id="master-agent" className="help-section">
						<Card>
							<div className="role-badge-header">
								<h2><i className="pi pi-shield"></i> Master Agent Features</h2>
								<span className="role-badge master-agent">
									{isSuperAgent ? 'SUPER AGENT' : 'MASTER AGENT'}
								</span>
							</div>
							<p className="section-intro">
								As a {isSuperAgent ? 'Super Agent' : 'Master Agent'}, you have additional capabilities for managing your team and customer assignments.
							</p>

							{/* Inviting an agent */}
							<div className="help-example">
								<h3>Inviting an Agent</h3>
								<p>
									Agents cannot sign themselves up. You create the account, and the person you are inviting
									chooses their own password &mdash; nobody, including you, ever sees or issues one for them.
								</p>
								<ol>
									<li>
										<strong>Invite Agent</strong> on the Agents page. Fill in their details and role, and the
										invitation email is sent automatically.
									</li>
									<li>
										They follow the link in that email, which carries a single-use token and is
										valid for <strong>seven days</strong>.
									</li>
									<li>
										They set a password of at least <strong>10 characters</strong>, and are signed in
										immediately. Their account becomes Active at that moment.
									</li>
								</ol>
								<div className="help-tip">
									<strong><i className="pi pi-info-circle"></i> The link works once.</strong> Once it has been
									used, or after seven days, it stops working. That is expected rather than a fault &mdash; send
									a fresh invitation instead.
								</div>
							</div>

							{/* Pending and resending */}
							<div className="help-example">
								<h3>Pending Invitations and Resending</h3>
								<ul>
									<li>
										An agent who has not finished registering shows as <strong>Pending</strong> in the Status
										column, with a Resend control beside it.
									</li>
									<li>
										<strong>Resending replaces the earlier link.</strong> Any previous invitation for that
										person stops working immediately, so ask them to use the newest email if they have several.
									</li>
									<li>
										Someone who already uses the EquityWorkx app receives a different email. They sign in with
										the password they already have and there is nothing new to set.
									</li>
									<li>
										A deactivated agent is not the same as a pending one. Reactivating an agent who never
										finished registering will still require an invitation.
									</li>
								</ul>
								<div className="help-tip">
									<strong><i className="pi pi-exclamation-triangle"></i> If an invitation does not arrive,</strong> check
									the address on the agent record first, then resend. The email goes out at the moment you create
									the agent, so a wrong address is the most common cause.
								</div>
							</div>

							{/* Manage Agents */}
							<div className="help-example">
								<h3>Manage Agents</h3>
								<p>Create, manage, and monitor agents in your organization.</p>
								<div className="help-screenshot">
									<div className="mock-agent-table">
										<div className="mock-table-header">
											<button className="mock-btn-primary">
												<i className="pi pi-plus"></i> Invite Agent
											</button>
										</div>
										<table className="mock-table">
											<thead>
												<tr>
													<th>Name</th>
													<th>Email</th>
													<th>Role</th>
													<th>Customers</th>
													<th>Status</th>
													<th>Actions</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td>John Smith</td>
													<td>john.smith@example.com</td>
													<td><span className="mock-role-badge agent">Agent</span></td>
													<td>24</td>
													<td><span className="mock-status-badge active">Active</span></td>
													<td>
														<button className="mock-action-btn"><i className="pi pi-pencil"></i></button>
														<button className="mock-action-btn"><i className="pi pi-eye"></i></button>
													</td>
												</tr>
												<tr>
													<td>Sarah Johnson</td>
													<td>sarah.j@example.com</td>
													<td><span className="mock-role-badge master">Master Agent</span></td>
													<td>18</td>
													<td><span className="mock-status-badge active">Active</span></td>
													<td>
														<button className="mock-action-btn"><i className="pi pi-pencil"></i></button>
														<button className="mock-action-btn"><i className="pi pi-eye"></i></button>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
								<div className="help-explanation">
									<div className="explanation-item">
										<strong><i className="pi pi-envelope" style={{ color: '#3498db' }}></i> Invite Agents:</strong> Click "Invite Agent" to send email invitations. New agents receive setup instructions with secure links.
									</div>
									<div className="explanation-item">
										<strong><i className="pi pi-users" style={{ color: '#2ecc71' }}></i> Manage Agents:</strong> View agent statistics and use action buttons to manage their role, status, and profile information.
									</div>
								</div>

								<div className="master-feature-details">
									<h4>Agent Management Capabilities:</h4>
									<ul>
										<li><strong>Invite Agents:</strong> Send email invitations to new agents with secure setup links</li>
										<li><strong>Manage Roles:</strong> Assign roles (Agent, Master Agent) to control access levels</li>
										<li><strong>View Performance:</strong> Monitor agent statistics including customer count and conversion rates</li>
										<li><strong>Activate/Deactivate:</strong> Control agent access to the portal</li>
										<li><strong>Edit Profiles:</strong> Update agent information and contact details</li>
									</ul>
								</div>
							</div>

							{/* Customer Assignments */}
							<div className="help-example">
								<h3>Customer Assignments</h3>
								<p>Manage how customers are assigned to agents for optimal coverage and workload balance.</p>
								<div className="help-screenshot">
									<div className="mock-assignment-interface">
										<div className="mock-assignment-header">
											<div className="mock-assignment-stats">
												<div className="mock-stat-box">
													<span className="stat-number">156</span>
													<span className="stat-label">Total Customers</span>
												</div>
												<div className="mock-stat-box">
													<span className="stat-number">23</span>
													<span className="stat-label">Unassigned</span>
												</div>
												<div className="mock-stat-box">
													<span className="stat-number">8</span>
													<span className="stat-label">Active Agents</span>
												</div>
											</div>
										</div>

										<div className="mock-assignment-panel">
											<div className="mock-customer-list">
												<h4>Unassigned Customers</h4>
												<div className="mock-customer-item">
													<input type="checkbox" />
													<div className="customer-info">
														<strong>Mike Chen</strong>
														<span>Lead Score: 78</span>
													</div>
												</div>
												<div className="mock-customer-item">
													<input type="checkbox" />
													<div className="customer-info">
														<strong>Amanda Lee</strong>
														<span>Lead Score: 65</span>
													</div>
												</div>
											</div>

											<div className="mock-assignment-action">
												<i className="pi pi-arrow-right"></i>
											</div>

											<div className="mock-agent-select">
												<h4>Assign To Agent</h4>
												<select className="mock-select">
													<option>John Smith (24 customers)</option>
													<option>Sarah Johnson (18 customers)</option>
													<option>Tom Wilson (31 customers)</option>
												</select>
												<button className="mock-btn-primary">Assign Selected</button>
											</div>
										</div>
									</div>
								</div>
								<div className="help-explanation">
									<div className="explanation-item">
										<strong><i className="pi pi-users" style={{ color: '#3498db' }}></i> Unassigned Customers:</strong> View all customers without an assigned agent, along with their lead scores to prioritize assignments.
									</div>
									<div className="explanation-item">
										<strong><i className="pi pi-check-square" style={{ color: '#2ecc71' }}></i> Bulk Assignment:</strong> Select multiple customers using checkboxes and assign them to an agent all at once. Current customer count for each agent is shown in the dropdown.
									</div>
								</div>

								<div className="master-feature-details">
									<h4>Assignment Features:</h4>
									<ul>
										<li><strong>View Unassigned:</strong> See all customers without an assigned agent</li>
										<li><strong>Bulk Assignment:</strong> Select multiple customers and assign them at once</li>
										<li><strong>Reassign Customers:</strong> Move customers between agents as needed</li>
										<li><strong>Load Balancing:</strong> View agent workloads to distribute customers evenly</li>
										<li><strong>Lead Score Visibility:</strong> Consider lead scores when making assignments</li>
										<li><strong>Automatic Notification:</strong> Agents are notified when customers are assigned to them</li>
									</ul>
								</div>
							</div>

							{/* System Settings (Super Agent Only) */}
							{isSuperAgent && (
								<div className="help-example">
									<h3>System Settings</h3>
									<div className="super-agent-note">
										<i className="pi pi-shield"></i>
										<div>
											<strong>Super Agent Exclusive</strong>
											<p>As a Super Agent, you have access to system-wide settings that affect all agents and the portal configuration. This includes:</p>
											<ul>
												<li>Portal configuration and branding</li>
												<li>System-wide notification settings</li>
												<li>Insight generation parameters</li>
												<li>Integration settings</li>
												<li>Security and compliance settings</li>
												<li>Mobile app subscription limits</li>
											</ul>
										</div>
									</div>

									<div className="help-subsection">
										<h4><i className="pi pi-mobile"></i> Mobile App Subscription Limits</h4>
										<p>Control how many free users can access the mobile app and set limits for premium subscriptions.</p>
										<div className="help-screenshot">
											<div className="mock-limits-panel">
												<div className="mock-limits-header">
													<h4>Subscription Limits</h4>
													<span className="mock-settings-badge">System Settings</span>
												</div>
												<div className="mock-limits-grid">
													<div className="mock-limit-item">
														<label>Free Tier User Limit</label>
														<div className="mock-limit-input">
															<input type="number" value="1000" readOnly />
															<span className="mock-limit-hint">Current: 847 users</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Monthly Premium Limit</label>
														<div className="mock-limit-input">
															<input type="number" value="500" readOnly />
															<span className="mock-limit-hint">Current: 234 subscribers</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Annual Premium Limit</label>
														<div className="mock-limit-input">
															<input type="number" value="200" readOnly />
															<span className="mock-limit-hint">Current: 89 subscribers</span>
														</div>
													</div>
												</div>
												<div className="mock-limits-actions">
													<button className="mock-btn-primary">Save Changes</button>
												</div>
											</div>
										</div>
										<div className="help-explanation">
											<div className="explanation-item">
												<strong><i className="pi pi-users" style={{ color: '#3498db' }}></i> Free Tier Limit:</strong> Maximum number of users who can use the mobile app on the free tier. When reached, new users must subscribe to premium.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-calendar" style={{ color: '#2ecc71' }}></i> Monthly Premium Limit:</strong> Maximum number of monthly premium subscriptions. Useful for controlling growth or promotional periods.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-star" style={{ color: '#f39c12' }}></i> Annual Premium Limit:</strong> Maximum number of annual premium subscriptions. Set to 0 for unlimited.
											</div>
										</div>
										<div className="master-feature-details">
											<h4>How to Set Subscription Limits:</h4>
											<ol>
												<li>Navigate to <strong>System Settings</strong> from the sidebar menu</li>
												<li>Select the <strong>Limits</strong> tab</li>
												<li>Enter the desired limits for each subscription tier</li>
												<li>Click <strong>Save Changes</strong> to apply</li>
												<li>Changes take effect immediately for new subscriptions</li>
											</ol>
											<p><em>Note: Existing subscriptions are not affected when limits are reduced. Limits only apply to new sign-ups.</em></p>
										</div>
									</div>

									<div className="help-subsection">
										<h4><i className="pi pi-calculator"></i> Calculator Limits</h4>
										<p>Control usage limits for the mortgage calculators in the mobile app.</p>
										<div className="help-screenshot">
											<div className="mock-limits-panel">
												<div className="mock-limits-header">
													<h4>Calculator Limits</h4>
													<span className="mock-settings-badge">System Settings</span>
												</div>
												<div className="mock-limits-grid">
													<div className="mock-limit-item">
														<label>Free Tier Daily Calculations</label>
														<div className="mock-limit-input">
															<input type="number" value="5" readOnly />
															<span className="mock-limit-hint">Per user per day</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Free Tier Monthly Calculations</label>
														<div className="mock-limit-input">
															<input type="number" value="50" readOnly />
															<span className="mock-limit-hint">Per user per month</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Premium Tier Limit</label>
														<div className="mock-limit-input">
															<input type="number" value="0" readOnly />
															<span className="mock-limit-hint">0 = Unlimited</span>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div className="help-explanation">
											<div className="explanation-item">
												<strong><i className="pi pi-clock" style={{ color: '#3498db' }}></i> Daily Limit:</strong> Maximum calculations a free user can perform per day. Resets at midnight.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-calendar" style={{ color: '#f39c12' }}></i> Monthly Limit:</strong> Maximum calculations a free user can perform per month. Resets on the 1st of each month.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-star" style={{ color: '#2ecc71' }}></i> Premium Limit:</strong> Set to 0 for unlimited calculations for premium subscribers.
											</div>
										</div>
									</div>

									<div className="help-subsection">
										<h4><i className="pi pi-lightbulb"></i> Insight Limits</h4>
										<p>Control how insights are generated and distributed across agents.</p>
										<div className="help-screenshot">
											<div className="mock-limits-panel">
												<div className="mock-limits-header">
													<h4>Insight Limits</h4>
													<span className="mock-settings-badge">System Settings</span>
												</div>
												<div className="mock-limits-grid">
													<div className="mock-limit-item">
														<label>Max Active Insights Per Agent</label>
														<div className="mock-limit-input">
															<input type="number" value="100" readOnly />
															<span className="mock-limit-hint">Current avg: 47 insights</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Max Insights Per Customer</label>
														<div className="mock-limit-input">
															<input type="number" value="10" readOnly />
															<span className="mock-limit-hint">Active at any time</span>
														</div>
													</div>
													<div className="mock-limit-item">
														<label>Insight Expiration (Days)</label>
														<div className="mock-limit-input">
															<input type="number" value="30" readOnly />
															<span className="mock-limit-hint">Auto-expire after</span>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div className="help-explanation">
											<div className="explanation-item">
												<strong><i className="pi pi-user" style={{ color: '#3498db' }}></i> Per Agent Limit:</strong> Maximum number of active insights an agent can have at once. Helps prevent overload.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-users" style={{ color: '#2ecc71' }}></i> Per Customer Limit:</strong> Maximum active insights per customer to prevent duplicate or excessive alerts.
											</div>
											<div className="explanation-item">
												<strong><i className="pi pi-clock" style={{ color: '#f39c12' }}></i> Expiration:</strong> Days until insights automatically expire if not actioned. Keeps queues fresh.
											</div>
										</div>
									</div>
								</div>
							)}

							<div className="help-tips">
								<h4><i className="pi pi-info-circle"></i> Best Practices</h4>
								<ul>
									<li><strong>Regular Review:</strong> Check agent performance weekly to identify support needs</li>
									<li><strong>Balance Workload:</strong> Aim for even customer distribution across agents</li>
									<li><strong>Quick Assignment:</strong> Assign new customers quickly so they receive timely follow-up</li>
									<li><strong>Consider Specialization:</strong> Assign based on agent expertise or territory</li>
									<li><strong>Monitor Conversions:</strong> Track which agents have the highest conversion rates</li>
									<li><strong>Provide Support:</strong> Use agent statistics to identify training opportunities</li>
								</ul>
							</div>
						</Card>
					</section>
				)}

				{/* Footer */}
				<div className="guide-footer">
					<Card>
						<h3>Need More Help?</h3>
						<p>If you have questions not covered in this guide, please contact support or your system administrator.</p>
						<div className="footer-actions">
							<button className="mock-btn-outline" onClick={() => navigate('/agent/dashboard')}>
								<i className="pi pi-home"></i> Back to Dashboard
							</button>
							<button className="mock-btn-outline" onClick={() => scrollToSection('dashboard-widgets')}>
								<i className="pi pi-arrow-up"></i> Back to Top
							</button>
						</div>
					</Card>
				</div>
			</div>

			{/* Floating Back to Top Button */}
			{showBackToTop && (
				<button
					className="back-to-top-button"
					onClick={scrollToTop}
					aria-label="Back to top"
				>
					<i className="pi pi-arrow-up"></i>
				</button>
			)}
		</div>
	);
};

export default PortalGuidePage;

